import { flow, getParent, types as t, IAnyModelType, Instance } from 'mobx-state-tree';

import { FilterQueryData, FiltersConjunctionType } from '@components/filters';
import { toQueryString } from '@utils/requests';

import { api } from 'api';
import { Application } from 'models/applications';
import { IApplication, IJob, IJobStage } from 'models/types';
import { LoadingState } from 'models/ui';
import { withEnv } from 'models/withEnv';
import { ApplicationRejectionMethod } from 'types/applications';
import { ILimitSearchResult } from 'types/search';

const PAGE_SIZE = 50;
const PAGE_SIZE_ALL = 5000;

const Sort = t.model('JobListViewSort', {
  key: t.string,
  order: t.enumeration('JobListViewSortOrder', ['descending', 'ascending']),
});

function toSortString(sort: Instance<typeof Sort>) {
  let str = sort.order === 'descending' ? '-' : '';

  switch (sort.key) {
    case 'numReviews':
      return str + 'num_rating';
    case 'averageReviews':
      return str + 'rating';
    case 'numNotes':
      return str + 'num_notes';
    case 'numMessages':
      return str + 'num_messages';
    case 'jobStageName':
      return str + 'stage';
    default:
      return str + sort.key;
  }
}

const LimitSearchResult = t
  .model('ApplicationSearchResult', {
    count: t.number,
    results: t.array(t.reference(t.late((): IAnyModelType => Application))),
    state: LoadingState,
  })
  .actions((self) => ({
    reset() {
      self.count = 0;
      self.results.clear();
    },
  }));

export const JobListView = t
  .model('JobListView', {
    applications: t.optional(LimitSearchResult, { count: 0, state: 'init', results: [] }),
    allApplicationsMatchingFilters: t.optional(LimitSearchResult, {
      count: 0,
      state: 'init',
      results: [],
    }),
    sort: t.optional(Sort, {
      key: 'date',
      order: 'descending',
    }),
    selected: t.map(t.reference(t.late((): IAnyModelType => Application))),

    clickedSelectAll: false,
    page: 1,
  })
  .views(withEnv)
  .views((self) => ({
    get job(): IJob {
      return getParent(self);
    },
    get jobId(): number {
      // @ts-ignore
      return self.job.id;
    },
    get selectedApplications() {
      return Array.from(self.selected.values());
    },
    get isFetching(): boolean {
      return (
        self.applications.state === 'loading' ||
        self.allApplicationsMatchingFilters.state === 'loading'
      );
    },
    get selectedAllOnPage(): boolean {
      return self.applications.results.every((application) => {
        return self.selected.has(application.id);
      });
    },
    get totalCount(): number {
      return self.applications.count;
    },
    get numPages(): number {
      return Math.ceil(self.applications.count / PAGE_SIZE);
    },
    get selectedCount(): number {
      return self.selected.size;
    },
    get hasSelectedAny(): boolean {
      return self.selected.size > 0;
    },
  }))
  .actions((self) => ({
    setPage(page: number) {
      self.page = page;
      this.load();
    },
    removeApplication(id: number) {
      const idx = self.applications.results.findIndex((a) => a.id === id);
      if (idx !== -1) {
        self.applications.results.splice(idx, 1);
      }
    },
    selectedAllMatching() {
      self.clickedSelectAll = true;
      this.loadAllMatchingFilters();
    },
    toggleSelectAll() {
      if (self.clickedSelectAll) {
        self.clickedSelectAll = false;
      }

      if (self.selected.size > 0) {
        self.selected.clear();
      } else {
        this.selectAll();
      }
    },
    onSettingsUpdate() {
      this.load(true);
    },
    clearSelected() {
      self.selected.clear();
      self.clickedSelectAll = false;
    },
    selectAll() {
      let obj = self.clickedSelectAll ? self.allApplicationsMatchingFilters : self.applications;
      for (let i = 0; i < obj.results.length; i++) {
        const application = obj.results[i];
        self.selected.set(application.id, application);
      }
    },
    toggleSelect(application: IApplication) {
      if (self.clickedSelectAll) {
        self.clickedSelectAll = false;
        const keys = Array.from(self.selected.keys());
        keys.forEach((key) => {
          if (!self.applications.results.find((a) => a.id === Number(key))) {
            self.selected.delete(key);
          }
        });
      }
      if (self.selected.has(application.id)) {
        self.selected.delete(application.id);
      } else {
        self.selected.set(application.id, application);
      }
    },
    setDataFromResponse(
      obj: Instance<typeof LimitSearchResult>,
      data: ILimitSearchResult<IApplication>,
    ) {
      obj.reset();
      let appIds: Array<number> = [];

      data.results.forEach((a) => {
        self.env.applicationStore.addOrUpdate(a, self.job.id);
        obj.results.push(a.id);
        appIds.push(a.id);
      });

      obj.count = data.count;
    },
    loadAllMatchingFilters: flow(function* () {
      self.allApplicationsMatchingFilters.state = 'loading';
      const { conjunction, data: queryData } = self.job.getSearchQueryData() as {
        conjunction: FiltersConjunctionType;
        data: FilterQueryData;
      };

      const method = conjunction === 'and' ? api.applications.search : api.applications.orSearch;

      const query = toQueryString({
        page_size: PAGE_SIZE_ALL,
        ordering: toSortString(self.sort),
        fixed__job: conjunction === 'or' ? self.job.id : null,
        job: conjunction === 'and' ? self.job.id : null,
        ...queryData,
      });

      try {
        const data: Awaited<ReturnType<typeof method>> = yield method(query, 1);
        // @ts-ignore
        self.setDataFromResponse(self.allApplicationsMatchingFilters, data);
        // @ts-ignore
        self.selectAll();
        self.allApplicationsMatchingFilters.state = 'loaded';
      } catch (e) {
        self.allApplicationsMatchingFilters.state = 'error';
      }
    }),
    load: flow(function* (overrideFirstPage?: boolean) {
      self.applications.state = 'loading';

      const { conjunction, data: queryData } = self.job.getSearchQueryData() as {
        conjunction: FiltersConjunctionType;
        data: FilterQueryData;
      };

      const method = conjunction === 'and' ? api.applications.search : api.applications.orSearch;

      const query = toQueryString({
        page_size: PAGE_SIZE,
        ordering: toSortString(self.sort),
        fixed__job: conjunction === 'or' ? self.job.id : null,
        job: conjunction === 'and' ? self.job.id : null,
        ...queryData,
      });

      try {
        const data: Awaited<ReturnType<typeof method>> = yield method(
          query,
          overrideFirstPage ? 1 : self.page,
        );
        // @ts-ignore
        self.setDataFromResponse(self.applications, data);
        // @ts-ignore
        self.resetSelection();
        self.applications.state = 'loaded';
      } catch (e) {
        self.applications.state = 'error';
      }
    }),
    resetSelection() {
      self.selected.clear();
      self.clickedSelectAll = false;
      self.allApplicationsMatchingFilters = LimitSearchResult.create({
        count: 0,
        results: [],
        state: 'init',
      });
    },
    reset() {
      this.resetSelection();
      self.applications = LimitSearchResult.create({
        count: 0,
        results: [],
        state: 'init',
      });
    },
    setSort(sort: typeof self.sort) {
      self.sort = sort;
      this.load();
    },
    updateStageForSelection(newStage: number) {
      const { data: queryData } = self.job.getSearchQueryData();
      const shouldRemove = queryData.jobStageId && !queryData.jobStageId.includes(String(newStage));

      if (shouldRemove) {
        self.applications.results = self.applications.results.filter(
          (a) => !self.selected.has(a.id),
        ) as typeof self.applications.results;
        self.selected.clear();
      } else {
        self.applications.results.forEach((application) => {
          if (self.selected.has(application.id)) {
            application.setStage(
              newStage,
              self.job.jobStages.find((stage: IJobStage) => stage.id === newStage).name,
            );
          }
        });
      }
    },
    updateRejectionForSelection(rejectionMethod: ApplicationRejectionMethod) {
      const { data: queryData } = self.job.getSearchQueryData();

      // The backend ANDs one query per `__terms` occurrence, so a value missing
      // from any one group is enough to drop the row out of the list.
      const anyOfGroups = (key: string): Array<Array<string>> =>
        ((queryData[key] as Array<string>) ?? []).map((value) => String(value).split('__'));

      const missingFromAnyGroup = (key: string, value: string): boolean => {
        const groups = anyOfGroups(key);
        return groups.length > 0 && groups.some((group) => !group.includes(value));
      };

      let shouldRemove = false;
      if (rejectionMethod !== 'reject') {
        // marked as notified, check labels
        shouldRemove =
          queryData.labels__exclude?.includes('notified') ||
          missingFromAnyGroup('labels__terms', 'notified');
      }

      if (!shouldRemove) {
        // Extra check if the labels didn't already determine the applications should be removed
        shouldRemove =
          queryData.status__exclude?.includes('rejected') ||
          (queryData.status__term && !queryData.status__term?.includes('rejected')) ||
          missingFromAnyGroup('status__terms', 'rejected');
      }

      if (shouldRemove) {
        self.applications.results = self.applications.results.filter(
          (a) => !self.selected.has(a.id),
        ) as typeof self.applications.results;
        self.selected.clear();
      }
    },
  }));

export type IJobListView = Instance<typeof JobListView>;
